import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../api/client';

/* ─── Icons ──────────────────────────────────────────────────── */
function EyeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}
function Spinner() {
  return (
    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

/* ─── Brand panel (shared design) ───────────────────────────── */
const securityPoints = [
  { title: 'Secure reset link', desc: 'Time-limited 6-digit reset codes sent to your verified email.' },
  { title: 'End-to-end encrypted', desc: 'All data is encrypted in transit with industry-standard TLS.' },
  { title: 'Instant access restored', desc: 'Regain access to your account in under 2 minutes.' },
];

function BrandPanel() {
  return (
    <div className="hidden lg:flex lg:w-[52%] bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 relative overflow-hidden flex-col justify-between p-14">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }}
        />
      </div>

      {/* Logo */}
      <div className="relative z-10 flex items-center gap-3">
        <img src="/logo.png" alt="Solaraith" className="h-14 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
        <span className="text-white text-lg font-bold tracking-tight">Solaraith Accounts</span>
      </div>

      {/* Hero */}
      <div className="relative z-10 flex-1 flex flex-col justify-center py-10">
        {/* Lock icon */}
        <div className="w-16 h-16 rounded-2xl bg-white/[0.07] border border-white/10 flex items-center justify-center mb-8">
          <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <span className="inline-flex items-center gap-2 text-blue-400 text-xs font-semibold uppercase tracking-widest mb-5">
          <span className="w-6 h-px bg-blue-500" />
          Account Recovery
        </span>
        <h2 className="text-[2.5rem] font-bold text-white leading-[1.15] mb-5">
          Recover your<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-violet-400">
            account securely
          </span>
        </h2>
        <p className="text-slate-400 text-base mb-10 leading-relaxed max-w-sm">
          We take account security seriously. Follow the steps to regain access safely.
        </p>
        <ul className="space-y-5">
          {securityPoints.map((point, i) => (
            <li key={i} className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-sm mt-0.5">
                {i + 1}
              </div>
              <div>
                <div className="text-white text-sm font-semibold mb-0.5">{point.title}</div>
                <div className="text-slate-400 text-xs leading-relaxed">{point.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="relative z-10">
        <p className="text-slate-600 text-xs">&copy; 2026 Solaraith Accounts. All rights reserved.</p>
      </div>
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────── */
const inputClass =
  'w-full px-4 py-3 rounded-2xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-slate-800 placeholder-slate-400 bg-white disabled:opacity-50 disabled:bg-slate-50 text-sm';

function Banner({ type, message }) {
  if (!message) return null;
  const styles = type === 'error'
    ? 'bg-red-50 border-red-100 text-red-700'
    : 'bg-emerald-50 border-emerald-100 text-emerald-700';
  const icon = type === 'error'
    ? 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    : 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
  return (
    <div className={`flex items-start gap-3 p-4 border rounded-2xl text-sm mb-6 ${styles}`}>
      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
      </svg>
      <span>{message}</span>
    </div>
  );
}

function PrimaryBtn({ loading, children, disabled, ...rest }) {
  return (
    <button
      disabled={loading || disabled}
      className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-2xl transition-all duration-200 shadow-lg shadow-blue-500/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
      {...rest}
    >
      {loading ? <><Spinner />{children}</> : children}
    </button>
  );
}

/* ─── Password strength indicator ───────────────────────────── */
function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    { label: '6+ characters', ok: password.length >= 6 },
    { label: 'Uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'Number', ok: /[0-9]/.test(password) },
    { label: 'Special character', ok: /[^a-zA-Z0-9]/.test(password) },
  ];
  const score = checks.filter((c) => c.ok).length;
  const bar = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-500'];
  const label = ['Weak', 'Fair', 'Good', 'Strong'];
  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < score ? bar[score - 1] : 'bg-slate-200'}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {checks.map((c) => (
          <span key={c.label} className={`flex items-center gap-1 text-xs ${c.ok ? 'text-emerald-600' : 'text-slate-400'}`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {c.ok
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />}
            </svg>
            {c.label}
          </span>
        ))}
      </div>
      {score > 0 && (
        <p className={`text-xs font-medium ${score <= 1 ? 'text-red-500' : score <= 2 ? 'text-orange-500' : score === 3 ? 'text-yellow-600' : 'text-emerald-600'}`}>
          Password strength: {label[score - 1]}
        </p>
      )}
    </div>
  );
}

/* ─── ForgotPassword component ───────────────────────────────── */
function ForgotPassword() {
  const [step, setStep] = useState(1); // 1=email, 2=code+new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  React.useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email.trim()) return setError('Please enter your email address');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSuccess('Reset code sent! Check your inbox.');
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSuccess('Code resent — check your inbox.');
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!code || code.length !== 6) return setError('Please enter the 6-digit reset code');
    if (newPassword.length < 6) return setError('Password must be at least 6 characters');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    setLoading(true);
    try {
      await authAPI.resetPassword(email, code, newPassword);
      setSuccess('Password reset successfully! You can now sign in with your new password.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. Please try again or request a new code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <BrandPanel />

      {/* ── Right: Form Panel ─────────────────────────────── */}
      <div className="flex-1 flex flex-col">
        {/* Mobile logo bar */}
        <div className="lg:hidden flex items-center gap-2 px-6 py-5 border-b border-slate-100 bg-white">
          <img src="/logo.png" alt="Solaraith" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="text-slate-800 font-bold text-base">Accounting Application</span>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-14 lg:px-16">
          <div className="w-full max-w-md">

            {/* Back to login */}
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium mb-8 hover:underline"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Sign In
            </Link>

            {step === 1 && (
              /* ── Step 1: Enter Email ─────────────────── */
              <>
                <div className="mb-9">
                  <div className="w-14 h-14 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center mb-5">
                    <svg className="w-7 h-7 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <h1 className="text-[1.85rem] font-bold text-slate-900 mb-1.5 tracking-tight">Reset your password</h1>
                  <p className="text-slate-500 text-sm">
                    Enter the email you registered with and we'll send you a reset code.
                  </p>
                </div>

                <Banner type="error" message={error} />
                <Banner type="success" message={success} />

                <form onSubmit={handleSendCode} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      disabled={loading}
                      className={inputClass}
                    />
                  </div>
                  <PrimaryBtn type="submit" loading={loading}>
                    Send Reset Code
                  </PrimaryBtn>
                </form>
              </>
            )}

            {step === 2 && (
              /* ── Step 2: Code + New Password ─────────── */
              <>
                <div className="mb-9">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-5">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h1 className="text-[1.85rem] font-bold text-slate-900 mb-1.5 tracking-tight">Create new password</h1>
                  <p className="text-slate-500 text-sm">
                    Enter the 6-digit code sent to{' '}
                    <span className="font-semibold text-slate-700">{email}</span>{' '}
                    and choose a new password.
                  </p>
                </div>

                <Banner type="error" message={error} />
                <Banner type="success" message={success} />

                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Reset Code</label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      required
                      disabled={loading}
                      className="w-full px-4 py-4 rounded-2xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-slate-800 bg-white disabled:opacity-50 text-2xl font-mono tracking-[0.5em] text-center"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min. 6 characters"
                        required
                        disabled={loading}
                        className={`${inputClass} pr-12`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </div>
                    <PasswordStrength password={newPassword} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm New Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      required
                      disabled={loading}
                      className={inputClass}
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1.5">Passwords do not match</p>
                    )}
                    {confirmPassword && newPassword === confirmPassword && newPassword.length >= 6 && (
                      <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Passwords match
                      </p>
                    )}
                  </div>

                  <PrimaryBtn
                    type="submit"
                    loading={loading}
                    disabled={code.length !== 6 || newPassword.length < 6 || newPassword !== confirmPassword}
                  >
                    Reset Password
                  </PrimaryBtn>
                </form>

                <div className="flex items-center justify-between mt-5">
                  <button
                    onClick={() => { setStep(1); setError(''); setSuccess(''); setCode(''); }}
                    className="text-sm text-slate-500 hover:text-slate-700 font-medium hover:underline flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Change email
                  </button>
                  <button
                    onClick={handleResendCode}
                    disabled={resendTimer > 0 || loading}
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold disabled:text-slate-400 disabled:cursor-not-allowed hover:underline"
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              /* ── Step 3: Success ────────────────────── */
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Password reset!</h1>
                <p className="text-slate-500 text-sm mb-8">
                  Your password has been reset successfully. You can now sign in with your new credentials.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-2xl transition-all duration-200 shadow-lg shadow-blue-500/20 active:scale-[0.98] text-sm"
                >
                  Sign In Now
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
